import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngxs/store';
import { AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { SchoolService } from '../../services/school/school.service';
import { UtilityService } from '../../services/utility/utility.service';
import { MessageService } from 'primeng/api';
import { ListSchoolNameBySearchTextResponse } from '../../models/schools/listSchoolNameBySearchTextResponse';
import { UserStateModel } from '../../states/user/user-state-model';
import { CalendarModule } from 'primeng/calendar';
import { ListMindlerAssessmentReportResponse, StudentTestLine } from '../../models/reporting/listMindlerAssessmentReportResponse';
import { AssessmentStatus } from '../../models/enums/assessmentStatus';
import { AssessmentType } from '../../models/enums/assessmentType';
import { UserSelectors } from '../../states/user/user-selector';
import { of, switchMap } from 'rxjs';
import { AuthRoles } from '../../models/enums/enums';
import { ReportingService } from '../../services/reporting/reporting.service';
import { ListMindlerAssessmentReportRequest } from '../../models/reporting/listMindlerAssessmentReportRequest';
import { DialogModule } from 'primeng/dialog';
import { MatchReport, MatchReportRespone } from '../../models/psychometric-test/psychometric-test.model';
import { PsychometricTestService } from '../../services/psychometric-test/psychometric-test.service';
import { InputTextModule } from 'primeng/inputtext';
import { SearchByNameResponse } from '../../models/account/searchByNameResponse';
import { SearchByEmailResponse } from '../../models/account/searchByEmailResponse';
import { ProgressBarModule } from 'primeng/progressbar';
import { PendingStudentsSyncStatusResponse, PendingStudent } from '../../models/reporting/pending-students-sync-status.model';
import { Router } from '@angular/router';

interface DropDownOption {
  name: string;
  value: AssessmentStatus
}

@Component({
  selector: 'app-reporting',
  standalone: true,
  imports: [
    FormsModule, ReactiveFormsModule,
    TableModule, DropdownModule,
    ButtonModule, AutoCompleteModule,
    CommonModule, CalendarModule,
    DialogModule, InputTextModule,
    ProgressBarModule],
  templateUrl: './reporting.component.html',
  styleUrl: './reporting.component.scss'
})
export class ReportingComponent implements OnInit{

  user: UserStateModel | undefined;
  totalRecords: number = 0;
  first: number = 0;
  rowSize: number = 10;
  pageSize = 10;
  filterForm!: FormGroup;
  schoolNames: ListSchoolNameBySearchTextResponse[] = [];
  names:SearchByNameResponse[] = []
  emails: SearchByEmailResponse[] = []   // GP-CB-02: Email filter suggestions
  selectedSchoolId!: number ;
  assessmentStatusOptions: DropDownOption[] = [];
  // GP-CB-02: Test Type filter. Clearing it (x) = All.
  assessmentTypeOptions = [
    { name: 'Stream', value: AssessmentType.Stream },
    { name: 'Career', value: AssessmentType.Career }
  ];
  readonly AssessmentStatus = AssessmentStatus;
  downloadReq: ListMindlerAssessmentReportRequest | undefined;;

  studentList: ListMindlerAssessmentReportResponse[] = [];
  matchReport?: MatchReportRespone;
  matchDialogHeader = 'Career/Stream Match Data';
    streams: MatchReport[] = [];
    trackByStreamId(index: number, stream: MatchReport): number {
      return stream.id;
    }

  isLoading: boolean = false;
  submitted: boolean = false;
  isVisible: boolean = false;

  // Sync related properties
  isSyncPopupVisible: boolean = false;
  isLoadingSyncStatus: boolean = false;
  isSyncing: boolean = false;
  syncStatus?: PendingStudentsSyncStatusResponse;
  processedCount: number = 0;
  totalToProcess: number = 0;
  syncProgress: number = 0;

  initializeForm(){
    this.filterForm = this.formBuilder.group({
      fromDate:[null],
      toDate: [null],
      studentName: [null],
      email: [null],
      highSchoolName: [null],
      assessmentState: [null],
      assessmentType: [null],
      class:[null],
      section:[null],
      academicSession:[null]
    });
  }
  constructor(
      private messageService: MessageService,
      private formBuilder: FormBuilder,
      private utilityService: UtilityService,
      private schoolService: SchoolService,
      private reportingService: ReportingService,
      private store: Store,
      private testService: PsychometricTestService,
      private router: Router
  ){}

  ngOnInit(): void {
    this.initializeForm();
    this.populateDropDownFromEnum();
    this.loadSessionOptions();
    this.getMindlerReports(1);

  }

  portal: string = '';

  /** Academic sessions in use (e.g. 2025-2026) for the Academic Session filter. A school admin only gets their own school's sessions. */
  sessionOptions: string[] = [];

  private loadSessionOptions(): void {
    this.reportingService.listAcademicSessions().subscribe({
      next: (response) => { this.sessionOptions = response?.result ?? []; },
      error: (err) => console.warn('Error loading sessions:', err),
    });
  }

  get isSuperAdmin(): boolean {
    return this.user?.role?.includes(AuthRoles.SuperAdmin) ?? false;
  }

  viewProfile(id: string): void{
    if (this.user!.role.includes(AuthRoles.SuperAdmin)) {
      this.portal = 'admin';
      this.router.navigate([this.portal,'view-profile', id]);
    }else if(this.user!.role.includes(AuthRoles.SchoolAdmin)){
      this.portal = 'school-portal';
      this.router.navigate([this.portal,'view-profile', id]);
    }

  }

  // GP-CB-02: Email filter suggestions - same behaviour as Student Name (after 3 characters).
  filterEmails(event: AutoCompleteCompleteEvent) {
    if (event.query.length > 2) {
      this.utilityService.searchEmailsByRole({
        roleName: 'Student',
        searchText: event.query
      }).subscribe({
        next: (response) => {
          this.emails = response.result
        }
      });
    } else {
      this.emails = [];
    }
  }

  filterNames(event: AutoCompleteCompleteEvent) {
    if (event.query.length > 2) {
      this.utilityService.searchNamesByRole({
        roleName: 'Student',
        searchText: event.query
      }).subscribe({
        next: (response) => {
          this.names = response.result
        }
      });
    } else {
      this.names = [];
    }
  }

  // Each report row is one test, so fetch the match data of that row's test (Stream or Career).
  getMatchReport(studentid: string, assessmentType?: AssessmentType | null){
    this.isLoading = true;
    this.isVisible = true;
    this.matchReport = undefined;
    this.streams = [];
    const testTypeName = this.getTestTypeName(assessmentType);
    this.matchDialogHeader = testTypeName ? `${testTypeName} Match Data` : 'Career/Stream Match Data';
    this.testService.getMatchReport(studentid, assessmentType).subscribe({
      next: (response) => {
        this.isLoading = false;;
        this.matchReport = response;
        this.streams = response.data || [];
      },
      error: (err) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error while processing request'
        });
      }
    })
  }

  populateDropDownFromEnum(){
    this.assessmentStatusOptions = Object.keys(AssessmentStatus).filter(key => isNaN(Number(key))).map(key => ({
      name: key,
      value: AssessmentStatus[key as keyof typeof AssessmentStatus]
    }))
  }

  filterSchoolNames(event: AutoCompleteCompleteEvent) {
    if (event.query.length > 3) {
      this.schoolService.listSchoolNameBySearchText(event.query).subscribe({
        next: (response) => {
          this.schoolNames = response.result
        }
      });
    } else {
      this.schoolNames = [];
    }
  }

  private getStudents(pageNumber: Number){
      this.isLoading = true;
      const listMindlerAssessmentReportRequest: ListMindlerAssessmentReportRequest = {
        startDate: this.filterForm.value.fromDate,
        endDate: this.filterForm.value.toDate,
        studentName: this.filterForm.value.studentName,
        schoolName: this.filterForm.value.highSchoolName,
        assessmentStatus: this.filterForm.value.assessmentState,
        pageNumber: pageNumber,
        pageSize: this.rowSize,
        class: this.filterForm.value.class || null,
        section: this.filterForm.value.section || null,
        assessmentType: this.filterForm.value.assessmentType ?? null,
        email: this.filterForm.value.email || null,
        academicSession: this.filterForm.value.academicSession || null
      }
      this.reportingService.listMindlerAssessmentReport(listMindlerAssessmentReportRequest).subscribe({
        next: (response) =>{
          this.studentList = this.withTestLines(response.result);
          this.totalRecords = response.total;
          this.isLoading = false;
        },
        error:(err) => {
          this.isLoading = false;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error fetching students' });
        }
      })
    }
    private getStudentsBySchool(pageNumber: Number){
      this.isLoading = true;
      const listMindlerAssessmentReportRequest: ListMindlerAssessmentReportRequest = {
        startDate: this.filterForm.value.fromDate,
        endDate: this.filterForm.value.toDate,
        studentName: this.filterForm.value.studentName,
        schoolName: this.filterForm.value.highSchoolName,
        assessmentStatus: this.filterForm.value.assessmentState,
        userId: this.user?.id,
        pageNumber: pageNumber,
        pageSize: this.rowSize,
        class: this.filterForm.value.class || null,
        section: this.filterForm.value.section || null,
        assessmentType: this.filterForm.value.assessmentType ?? null,
        email: this.filterForm.value.email || null,
        academicSession: this.filterForm.value.academicSession || null
      }
      this.reportingService.listMindlerAssessmentReport(listMindlerAssessmentReportRequest).subscribe({
        next: (response) =>{
          this.studentList = this.withTestLines(response.result);
          this.totalRecords = response.total;
          this.isLoading = false;
        },
        error:(err) => {
          this.isLoading = false;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error fetching students' });
        }
      })
    }

  getMindlerReports(pageNumber: Number) {
    this.store.select(UserSelectors.getUser).pipe(
      switchMap(user => {
        if (!user || !user.id) {
          return of(null);
        }
        this.user = user;
        return of(user); // Ensure an observable is returned
      })
    ).subscribe({
      next: (response) => {
        if (response != null) {
          if (response.role.includes(AuthRoles.SuperAdmin)) {
            this.getStudents(pageNumber);
            this.downloadReq = {
              startDate: this.filterForm.value.fromDate,
              endDate: this.filterForm.value.toDate,
              studentName: this.filterForm.value.studentName,
              schoolName: this.filterForm.value.highSchoolName,
              assessmentStatus: this.filterForm.value.assessmentState,
              pageNumber: pageNumber,
              pageSize: this.rowSize,
              class: this.filterForm.value.class || null,
              section: this.filterForm.value.section || null,
              assessmentType: this.filterForm.value.assessmentType ?? null,
              email: this.filterForm.value.email || null,
              academicSession: this.filterForm.value.academicSession || null
            }
          }
          else if (response.role.includes(AuthRoles.SchoolAdmin)) {
            this.getStudentsBySchool(pageNumber);
            this.downloadReq = {
              startDate: this.filterForm.value.fromDate,
              endDate: this.filterForm.value.toDate,
              studentName: this.filterForm.value.studentName,
              schoolName: this.filterForm.value.highSchoolName,
              assessmentStatus: this.filterForm.value.assessmentState,
              userId: response.id,
              pageNumber: pageNumber,
              pageSize: this.rowSize,
              class: this.filterForm.value.class || null,
              section: this.filterForm.value.section || null,
              assessmentType: this.filterForm.value.assessmentType ?? null,
              email: this.filterForm.value.email || null,
              academicSession: this.filterForm.value.academicSession || null
            }
          }
        }
      },
      error: (error) => {
        console.warn('Error fetching user data', error);
      }
    })
  }


  downloadStudentListExcel() {
    this.reportingService.downloadAssessmentReport(this.downloadReq!).subscribe({
      next: (resp) => {
        const cd = resp.headers.get('Content-Disposition');
        const filename = this.getFilenameFromContentDisposition(cd) || 'assessment-reportt.xlsx';
        const blob = new Blob([resp.body!], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});
        this.saveBlobAsFile(blob, filename);
      },
      error: (err) => console.error('Download failed', err)
    });
  }

  getFilenameFromContentDisposition(contentDisposition: string | null): string | null {
    if (contentDisposition && contentDisposition.indexOf('attachment') !== -1) {
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = filenameRegex.exec(contentDisposition);
      if (matches != null && matches.length > 1) {
        return matches[1].replace(/['"]/g, '');
      }
    }
    return null;
  }
  saveBlobAsFile(blob: Blob | null, filename: string): void {
    if (blob) {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }
  }

  getTestTypeName(type?: AssessmentType | null): string {
    switch (type) {
      case AssessmentType.Stream:
        return 'Stream';
      case AssessmentType.Career:
        return 'Career';
      default:
        return '';
    }
  }

  // GP-CB-02: one row per student - build the Stream / Career lines shown inside the row.
  private withTestLines(students: ListMindlerAssessmentReportResponse[]): ListMindlerAssessmentReportResponse[] {
    return (students ?? []).map(student => {
      const tests: StudentTestLine[] = [];
      if (student.streamTestStatus != null) {
        tests.push({
          type: AssessmentType.Stream,
          label: this.getTestTypeName(AssessmentType.Stream),
          status: student.streamTestStatus,
          academicSession: student.streamAcademicSession,
          summaryReportUrl: student.streamSummaryReportUrl,
          detailReportUrl: student.streamDetailReportUrl,
          reportAnalysis: student.streamReportAnalysis
        });
      }
      if (student.careerTestStatus != null) {
        tests.push({
          type: AssessmentType.Career,
          label: this.getTestTypeName(AssessmentType.Career),
          status: student.careerTestStatus,
          academicSession: student.careerAcademicSession,
          summaryReportUrl: student.careerSummaryReportUrl,
          detailReportUrl: student.careerDetailReportUrl,
          reportAnalysis: student.careerReportAnalysis
        });
      }
      return { ...student, tests };
    });
  }

  getTestStatusName(status: AssessmentStatus){
    switch(status){
      case 0:
        return 'Not Started'

      case 1:
        return 'In Progress'

      case 2:
      return 'Completed'

      default:
        return 'Not Started'
    }

  }

  onLazyLoad($event: TableLazyLoadEvent): void {
    const pageNumber = Math.floor(($event.first ?? 0) / ($event.rows ?? this.rowSize)) + 1;
    this.first = $event.first ?? 0;
    this.rowSize = $event.rows ?? this.rowSize;
    this.getMindlerReports(pageNumber);
  }

  // Sync related methods
  openSyncPopup(): void {
    this.isSyncPopupVisible = true;
    this.loadSyncStatus();
  }

  loadSyncStatus(): void {
    this.isLoadingSyncStatus = true;
    this.reportingService.getPendingStudentsSyncStatus().subscribe({
      next: (response) => {
        this.syncStatus = response;
        this.isLoadingSyncStatus = false;
      },
      error: (error) => {
        this.isLoadingSyncStatus = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load sync status'
        });
      }
    });
  }

  downloadPendingData(): void {
    if (!this.syncStatus?.pendingStudents?.length) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'No pending data to download'
      });
      return;
    }

    // Create Excel data
    const excelData = this.syncStatus.pendingStudents.map(student => ({
      'Student ID': student.studentId,
      'Student Name': student.studentName,
      'Email': student.email,
      'Phone Number': student.phoneNumber,
      'Created On': student.createdOn,
      'Assessment Completed': student.isAssessmentCompleted ? 'Yes' : 'No',
      'Assessment Percentage': student.assessmentPercentage,
      'Status': student.status
    }));

    // Convert to CSV and download
    this.downloadCSV(excelData, 'pending-students-data');
  }

  private downloadCSV(data: any[], filename: string): void {
    const csvContent = this.convertToCSV(data);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private convertToCSV(data: any[]): string {
    if (!data.length) return '';

    const headers = Object.keys(data[0]);
    const csvRows = [headers.join(',')];

    for (const row of data) {
      const values = headers.map(header => {
        const value = row[header];
        return `"${value}"`;
      });
      csvRows.push(values.join(','));
    }

    return csvRows.join('\n');
  }

  startSyncProcess(): void {
    if (!this.syncStatus?.pendingStudents?.length) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'No pending students to sync'
      });
      return;
    }

    this.isSyncing = true;
    this.processedCount = 0;
    this.totalToProcess = this.syncStatus.pendingStudents.length;
    this.syncProgress = 0;

    // Process students in batches of 10
    const studentIds = this.syncStatus.pendingStudents.map(student => student.studentId);
    const batches = this.chunkArray(studentIds, 10);

    this.processBatches(batches, 0);
  }

  private chunkArray<T>(array: T[], size: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  }

  private processBatches(batches: string[][], batchIndex: number): void {
    if (batchIndex >= batches.length) {
      // All batches processed
      this.isSyncing = false;
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Sync process completed successfully'
      });
      // Refresh sync status
      this.loadSyncStatus();
      return;
    }

    const currentBatch = batches[batchIndex];
    const request = {
      studentIds: currentBatch,
      forceProcess: true
    };

    this.reportingService.processStudentsBatch(request).subscribe({
      next: (response) => {
        // Update progress
        this.processedCount += currentBatch.length;
        this.syncProgress = (this.processedCount / this.totalToProcess) * 100;

        // Process next batch
        setTimeout(() => {
          this.processBatches(batches, batchIndex + 1);
        }, 1000); // 1 second delay between batches
      },
      error: (error) => {
        this.isSyncing = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to process batch. Please try again.'
        });
      }
    });
  }
}